import { CONSENT_PARAM, FAILURE_PARAM, bankFailureMessage } from '@/api/bank-consent'

/**
 * What the bank callback left in the address bar, read back.
 *
 * The parameters are imported from the module the callback writes them with
 * rather than spelled again here: the server has always answered, and for a
 * while nothing on this side listened, which left a bank that failed to connect
 * and one that succeeded looking exactly alike.
 *
 * Nothing here touches the browser, so the server's own tests can hold this
 * against the route that writes into it rather than against a copy of the
 * strings.
 */
export type BankOutcome =
  | { kind: 'granted'; reference: string }
  | { kind: 'failed'; failure: string; message: string }

export function bankOutcomeOf(search: string): BankOutcome | undefined {
  const params = new URLSearchParams(search)

  // Read before the consent: the callback writes one or the other, and a URL
  // carrying both is not one this deployment produced.
  const failure = params.get(FAILURE_PARAM)
  if (failure !== null && failure.length > 0) {
    return { kind: 'failed', failure, message: bankFailureMessage(failure) }
  }

  const reference = params.get(CONSENT_PARAM)
  if (reference !== null && reference.length > 0) return { kind: 'granted', reference }

  return undefined
}

/**
 * The attribution form a granted consent belongs to, so the announcement can
 * put the reader in front of the accounts it is asking them about.
 */
export function attributionId(reference: string): string {
  return `nw-consent-${reference}`
}
